// src/components/SummaryTable.js
import React from 'react';
import './SummaryTable.css';

// Fungsi untuk format angka menjadi format mata uang Rupiah
const formatCurrency = (number) => {
  // Cek jika number tidak valid, kembalikan Rp 0
  if (number === null || typeof number === 'undefined' || isNaN(number)) {
    return (
      <span style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Rp</span>
        <span>0</span>
      </span>
    );
  }
  const formatted = new Intl.NumberFormat('id-ID').format(number);
  return (
    <span style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>Rp</span>
      <span>{formatted}</span>
    </span>
  );
};

// Fungsi untuk format angka biasa dengan pemisah ribuan
const formatNumber = (number) => {
  // Cek jika number tidak valid, kembalikan 0
  if (number === null || typeof number === 'undefined' || isNaN(number)) {
    return '0';
  }
  return new Intl.NumberFormat('id-ID').format(number);
};

// Fungsi untuk format angka desimal (untuk average)
const formatDecimal = (number) => {
  if (number === null || typeof number === 'undefined' || isNaN(number)) {
    return '0,00';
  }
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

// Fungsi untuk format angka persentase (untuk PSR)
const formatPercent = (number) => {
  if (number === null || typeof number === 'undefined' || isNaN(number) || number === 0) {
    return '0,00%';
  }
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number) + '%';
};

// Tooltip component for fee headers
const FeeTooltip = ({ label, tooltipText }) => (
  <span className="fee-tooltip-wrapper">
    {label}
    <span className="fee-tooltip-icon">ⓘ</span>
    <span className="fee-tooltip-text">{tooltipText}</span>
  </span>
);

// Tooltip component for PSR headers
const PsrTooltip = ({ label, tooltipText }) => (
  <span className="fee-tooltip-wrapper psr-tooltip-wrapper">
    {label}
    <span className="fee-tooltip-icon">ⓘ</span>
    <span className="fee-tooltip-text">{tooltipText}</span>
  </span>
);

function SummaryTable({ title, data, showAverage, showFee, showPsr }) {
  // Hitung total untuk semua kolom di footer
  const totals = data.reduce((acc, item) => {
    acc.b2b_amount += item.b2b_amount || 0;
    acc.b2b_entries += item.b2b_entries || 0;
    acc.b2b_count += item.b2b_count || 0;
    acc.b2b_fee += item.b2b_fee || 0;
    acc.b2c_amount += item.b2c_amount || 0;
    acc.b2c_entries += item.b2c_entries || 0;
    acc.b2c_count += item.b2c_count || 0;
    acc.b2c_fee += item.b2c_fee || 0;
    acc.total_amount += item.total_amount || 0;
    acc.total_entries += item.total_entries || 0;
    acc.total_count += item.total_count || 0;
    acc.total_fee += item.total_fee || 0;
    // Sum of all vendor averages
    acc.sum_avg_amount += item.avg_amount || 0;
    acc.sum_avg_entries += item.avg_entries || 0;
    // Get days_in_range (should be same for all vendors)
    if (!acc.days_in_range && item.days_in_range) {
      acc.days_in_range = item.days_in_range;
    }
    return acc;
  }, {
    b2b_amount: 0, b2b_entries: 0, b2b_count: 0, b2b_fee: 0,
    b2c_amount: 0, b2c_entries: 0, b2c_count: 0, b2c_fee: 0,
    total_amount: 0, total_entries: 0, total_count: 0, total_fee: 0,
    sum_avg_amount: 0, sum_avg_entries: 0,
    days_in_range: 0
  });

  const b2bTotalPsr = totals.b2b_count > 0 ? (totals.b2b_entries / totals.b2b_count * 100) : 0;
  const b2cTotalPsr = totals.b2c_count > 0 ? (totals.b2c_entries / totals.b2c_count * 100) : 0;
  const grandTotalPsr = totals.total_count > 0 ? (totals.total_entries / totals.total_count * 100) : 0;

  // Dynamic column spans
  let b2bColSpan = 2;
  if (showFee) b2bColSpan += 1;
  if (showPsr) b2bColSpan += 1;

  let b2cColSpan = 2;
  if (showFee) b2cColSpan += 1;
  if (showPsr) b2cColSpan += 1;

  let totalColSpan = 2; // Amount + Entries always shown
  if (showFee) totalColSpan += 1; // Fee Total
  if (showPsr) totalColSpan += 1; // Total PSR%
  if (showAverage) totalColSpan += 2; // Avg Amount + Avg Entries

  return (
    <div className="summary-container">
      {/* Judul sekarang dikelola oleh halaman induk (SummaryPage.js) */}
      <table className="summary-table">
        <thead>
          <tr>
            <th rowSpan="2">VENDOR</th>
            <th colSpan={b2bColSpan}>B2B</th>
            <th colSpan={b2cColSpan}>B2C</th>
            <th colSpan={totalColSpan}>TOTAL</th>
          </tr>
          <tr>
            <th>Amount</th>
            <th>Entries</th>
            {showFee && (
              <th>
                <FeeTooltip label="Fee" tooltipText="B2B Fee = 1% dari Amount" />
              </th>
            )}
            {showPsr && (
              <th>
                <PsrTooltip label="PSR%" tooltipText="B2B PSR% = B2B Paid Entries / B2B Created Invoices" />
              </th>
            )}
            <th>Amount</th>
            <th>Entries</th>
            {showFee && (
              <th>
                <FeeTooltip label="Fee" tooltipText="B2C Fee = 0,5% dari Amount" />
              </th>
            )}
            {showPsr && (
              <th>
                <PsrTooltip label="PSR%" tooltipText="B2C PSR% = B2C Paid Entries / B2C Created Invoices" />
              </th>
            )}
            <th>Amount</th>
            <th>Entries</th>
            {showFee && (
              <th>
                <FeeTooltip label="Fee Total" tooltipText="Fee Total = B2B Fee (1%) + B2C Fee (0,5%)" />
              </th>
            )}
            {showPsr && (
              <th>
                <PsrTooltip label="PSR%" tooltipText="Total PSR% = Total Paid Entries / Total Created Invoices" />
              </th>
            )}
            {showAverage && <th>Avg Amount</th>}
            {showAverage && <th>Avg Entries</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const b2bPsr = item.b2b_count > 0 
              ? (item.b2b_entries / item.b2b_count * 100) 
              : (item.b2b_psr || 0);
            const b2cPsr = item.b2c_count > 0 
              ? (item.b2c_entries / item.b2c_count * 100) 
              : (item.b2c_psr || 0);
            const totalPsr = item.total_count > 0 
              ? (item.total_entries / item.total_count * 100) 
              : (item.psr || 0);

            return (
              <tr key={item.vendor}>
                <td>{item.vendor}</td>
                {/* B2B Columns */}
                <td>{formatCurrency(item.b2b_amount)}</td>
                <td>{formatNumber(item.b2b_entries)}</td>
                {showFee && <td className="fee-cell">{formatCurrency(item.b2b_fee)}</td>}
                {showPsr && <td className="psr-cell">{formatPercent(b2bPsr)}</td>}
                {/* B2C Columns */}
                <td>{formatCurrency(item.b2c_amount)}</td>
                <td>{formatNumber(item.b2c_entries)}</td>
                {showFee && <td className="fee-cell">{formatCurrency(item.b2c_fee)}</td>}
                {showPsr && <td className="psr-cell">{formatPercent(b2cPsr)}</td>}
                {/* TOTAL Columns */}
                <td>{formatCurrency(item.total_amount)}</td>
                <td>{formatNumber(item.total_entries)}</td>
                {showFee && <td className="fee-cell">{formatCurrency(item.total_fee)}</td>}
                {showPsr && <td className="psr-cell">{formatPercent(totalPsr)}</td>}
                {showAverage && <td>{formatCurrency(item.avg_amount)}</td>}
                {showAverage && <td>{formatDecimal(item.avg_entries)}</td>}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>TOTAL</td>
            {/* B2B Totals */}
            <td>{formatCurrency(totals.b2b_amount)}</td>
            <td>{formatNumber(totals.b2b_entries)}</td>
            {showFee && <td className="fee-cell">{formatCurrency(totals.b2b_fee)}</td>}
            {showPsr && <td className="psr-cell">{formatPercent(b2bTotalPsr)}</td>}
            {/* B2C Totals */}
            <td>{formatCurrency(totals.b2c_amount)}</td>
            <td>{formatNumber(totals.b2c_entries)}</td>
            {showFee && <td className="fee-cell">{formatCurrency(totals.b2c_fee)}</td>}
            {showPsr && <td className="psr-cell">{formatPercent(b2cTotalPsr)}</td>}
            {/* GRAND Totals */}
            <td>{formatCurrency(totals.total_amount)}</td>
            <td>{formatNumber(totals.total_entries)}</td>
            {showFee && <td className="fee-cell">{formatCurrency(totals.total_fee)}</td>}
            {showPsr && <td className="psr-cell">{formatPercent(grandTotalPsr)}</td>}
            {showAverage && <td>{formatCurrency(totals.sum_avg_amount)}</td>}
            {showAverage && <td>{formatDecimal(totals.sum_avg_entries)}</td>}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default SummaryTable;

